import { clsx } from 'clsx';

export interface ToggleProps {
  /** Whether the toggle is on */
  checked: boolean;
  /** Callback when the toggle value changes */
  onChange: (checked: boolean) => void;
  /** Visual size */
  size?: 'sm' | 'md';
  /** Disable interaction */
  disabled?: boolean;
  /** Optional text label rendered beside the toggle */
  label?: string;
}

const SIZE_MAP = {
  sm: { track: 'h-4 w-7', thumb: 'h-3 w-3', translate: 'translate-x-3' },
  md: { track: 'h-5 w-9', thumb: 'h-3.5 w-3.5', translate: 'translate-x-4' },
};

export function Toggle({ checked, onChange, size = 'md', disabled = false, label }: ToggleProps) {
  const s = SIZE_MAP[size];

  const toggle = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
        'transition-colors duration-200 ease-in-out',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-surface-2',
        s.track,
      )}
    >
      <span
        aria-hidden="true"
        className={clsx(
          'pointer-events-none inline-block rounded-full bg-white shadow-xs ring-0',
          'transition-transform duration-200 ease-in-out',
          checked ? s.translate : 'translate-x-0',
          s.thumb,
        )}
      />
    </button>
  );

  if (!label) return toggle;

  return (
    <label
      className={clsx(
        // min-h-11: the whole labelled row is the 44px tap target (a click on a label presses its switch).
        'inline-flex min-h-11 items-center gap-2 text-2xs font-medium text-ink',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {toggle}
      {label}
    </label>
  );
}
